import { useCallback, useRef, useState } from 'react'
import { AxiosError } from 'axios'
import axiosRequest from 'lib/axios/api-request'
import uniqueId from 'lodash/uniqueId'

import { ResponseData, ResponseMeta, ResponseErrors } from 'lib/types'

export const isAxiosError = (
  error: unknown
): error is AxiosError<{ errors: ResponseErrors }> =>
  (error as AxiosError<{ errors: ResponseErrors }>).response?.data?.errors !==
  undefined

type InitialState<T> = {
  data?: T
  isLoading?: boolean
}

export type StateType<T = ResponseData> = {
  data: T | null
  meta: ResponseMeta | null
  errors: ResponseErrors
  isLoading: boolean
}

export type MakeRequestFn = <Type>(config: {
  url: string
  method?: string
  data?: { [key: string]: unknown }
  headers?: { [key: string]: string }
  sendToken?: boolean
  timeout?: number
  silent?: boolean
}) => Promise<Type>

export type ResetFn = () => void

const useApi = <T = ResponseData>(
  initialState: InitialState<T> = {}
): [StateType<T>, MakeRequestFn, ResetFn] => {
  const [data, setData] = useState<T | null>(initialState.data ?? null)
  const [meta, setMeta] = useState(null)
  const [errors, setErrors] = useState<ResponseErrors>([])
  const [isLoading, setIsLoading] = useState(initialState.isLoading || false)
  const lastRequestId = useRef<string>()

  const reset = useCallback(() => {
    // Invalidate any in-flight request so its response can't overwrite the
    // state we're about to reset once it resolves.
    lastRequestId.current = uniqueId('use-api-reset-')
    setData(initialState.data ?? null)
    setMeta(null)
    setErrors([])
    setIsLoading(initialState.isLoading || false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const makeRequest = useCallback(async (config) => {
    const requestId = uniqueId('use-api-')
    lastRequestId.current = requestId

    setIsLoading(true)
    setErrors([])

    try {
      const response = await axiosRequest(config)

      if (lastRequestId.current !== requestId) {
        return response.data
      }

      setData(response.data.data)
      setMeta(response.data.meta)
      setIsLoading(false)
      return response.data
    } catch (error) {
      const errors = isAxiosError(error)
        ? (error.response?.data?.errors ?? [])
        : []
      setErrors(errors)
      setIsLoading(false)
      throw error
    }
  }, [])

  return [{ data, errors, isLoading, meta }, makeRequest, reset]
}

export default useApi
